"use client"

import { Head, Link } from "@inertiajs/react"
import {
  Edit02,
  File01,
  Film01,
  Headphones01,
  Image01,
  LinkExternal01,
} from "@untitledui/icons"

import { TextHTML } from "@/components/playlists"
import { RelatedTrackCard } from "@/components/tracks"
import { Badge } from "@/components/ui/badge"
import { Breadcrumbs } from "@/components/ui/breadcrumbs"
import { Button } from "@/components/ui/button"
import { TagBadgeGroup } from "@/components/ui/tag-badge"
import { AuthedShell } from "@/layouts/authed-shell"
import { formatDuration } from "@/lib/utils/"
import type { Playlist, Track, TrackSummary } from "@/types"
import { ContentAspectEnum, ContentBodyTypeEnum } from "@/types"

import getEmbedUrl from "./_helpers/get-embed-url"

interface TrackShowProps {
  playlist: Playlist
  track: Track
  relatedTracks: TrackSummary[]
  canEdit: boolean
}

const contentTypeIcons = {
  VideoBody: Film01,
  AudioBody: Headphones01,
  PhotoBody: Image01,
  ArticleBody: File01,
}
/**
 * Track Show component.
 * @param playlist - The playlist.
 * @param track - The track.
 * @param canEdit - Whether the current user may maintain the playlist (owner or admin).
 * @returns The Track Show component.
 */
export default function TrackShow({
  playlist,
  track,
  relatedTracks,
  canEdit,
}: TrackShowProps) {
  const { post } = track
  if (!post) {
    return (
      <AuthedShell headerVariant="light">
        <Head title="Track" />
        <div className="editorial max-w-container mx-auto px-4 py-12 text-center lg:px-8">
          <p className="text-secondary">This track is unavailable.</p>
        </div>
      </AuthedShell>
    )
  }
  const { content_body } = post
  const contentType = content_body?.type
  const ContentIcon =
    contentType && contentType in contentTypeIcons
      ? contentTypeIcons[contentType as keyof typeof contentTypeIcons]
      : File01
  const aspectLabel = content_body?.aspect
    ? content_body.aspect === ContentAspectEnum.ratio1x1
      ? "1:1"
      : content_body.aspect === ContentAspectEnum.ratio4x3
        ? "4:3"
        : content_body.aspect === ContentAspectEnum.ratio4x5
          ? "4:5"
          : content_body.aspect === ContentAspectEnum.ratio9x16
            ? "9:16"
            : content_body.aspect === ContentAspectEnum.ratio16x9
              ? "16:9"
              : String(content_body.aspect)
    : null

  // Metadata echoed into the companion aside (additive, mirrors the "About
  // this list" card on playlists#show).
  const contentTypeLabel = contentType ? contentType.replace("Body", "") : null
  const durationLabel = content_body?.duration_seconds
    ? `${formatDuration(content_body.duration_seconds)} mins`
    : null
  const readingLabel =
    contentType === ContentBodyTypeEnum.article && content_body?.reading_time
      ? `${content_body.reading_time} mins`
      : null

  return (
    <AuthedShell headerVariant="brand">
      <Head title={post.title} />
      <section className="bg-secondary mb-3 pt-6 pb-8">
        <div className="max-w-container mx-auto px-4 lg:px-8">
          {/* Breadcrumbs */}
          <div>
            <Breadcrumbs maxVisibleItems={4} />
          </div>
        </div>
      </section>

      <div className="max-w-container mx-auto flex min-h-[calc(80vh)] w-full flex-1 flex-col gap-6 px-4 lg:px-8">
        {/* Track header */}
        <div className="flex flex-col gap-6 md:flex-row md:gap-8">
          {/* Thumbnail */}
          <div className="bg-secondary_subtle aspect-video w-full shrink-0 overflow-hidden rounded-xl md:w-80 lg:w-96">
            {post.thumbnail_url ? (
              <img
                src={post.thumbnail_url}
                alt={post.title}
                className="size-full object-cover"
              />
            ) : (
              <div className="flex size-full items-center justify-center">
                <ContentIcon className="text-quaternary size-16" />
              </div>
            )}
          </div>

          {/* Track info */}
          <div className="flex flex-1 flex-col gap-4">
            <div className="flex flex-col gap-2">
              <div className="flex items-start justify-between gap-4">
                <h1 className="text-display-xs text-primary lg:text-display-sm font-semibold">
                  {post.title}
                </h1>
              </div>

              {post.creator && (
                <p className="text-md text-tertiary">By {post.creator}</p>
              )}
            </div>

            {/* Meta info */}
            <div className="flex flex-wrap items-center gap-3">
              {contentType && (
                <Badge size="sm" color="gray" type="modern">
                  {contentType.replace("Body", "")}
                </Badge>
              )}
              {content_body?.source && (
                <Badge size="sm" color="gray" type="modern">
                  {content_body.source}
                </Badge>
              )}
              {aspectLabel && (
                <Badge size="sm" color="gray" type="modern">
                  {aspectLabel}
                </Badge>
              )}
            </div>

            {/* Duration in seconds */}
            {content_body.duration_seconds && (
              <div>
                <p className="text-secondary text-sm">
                  {`Estimated Watching: ${formatDuration(content_body?.duration_seconds)}mins`}
                </p>
              </div>
            )}

            {/* Reading Time */}
            {contentType === ContentBodyTypeEnum.article &&
              content_body.reading_time && (
                <div>
                  <p className="text-secondary text-sm">
                    {`Estimated Reading: ${content_body.reading_time}mins`}
                  </p>
                </div>
              )}

            {/* Tags — the post's tags (same source as playlist tags) */}
            <TagBadgeGroup tags={track.tags} />

            {/* Actions */}
            {post.canonical_url && (
              <div className="flex gap-3">
                <Button
                  onClick={() =>
                    window.open(
                      post.canonical_url!,
                      "_blank",
                      "noopener,noreferrer",
                    )
                  }
                  color="secondary"
                  size="sm"
                  iconLeading={LinkExternal01}
                >
                  View Original
                </Button>
                {canEdit && (
                  <Button
                    href={`/playlists/${playlist.slug || playlist.id}/tracks/${track.id}/edit`}
                    color="secondary"
                    size="sm"
                    iconLeading={Edit02}
                  >
                    Edit Track
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Track details run in a constrained column beside an "About this
            track" companion aside — the same tactile treatment as
            playlists#show. Below `lg` the aside folds beneath the content. */}
        <div className="grid grid-cols-1 gap-x-10 gap-y-10 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start lg:gap-y-0">
          <div className="flex min-w-0 flex-col gap-6">
            {/* Content Body Details */}
            {content_body && (
              <div className="border-secondary bg-primary rounded-xl border p-6">
                <div className="flex flex-col gap-4">
                  {/* Video Body / Audio Body  */}
                  {(contentType == ContentBodyTypeEnum.video ||
                    contentType == ContentBodyTypeEnum.audio) && (
                    <>
                      {post.canonical_url &&
                        (() => {
                          const embedUrl = getEmbedUrl(
                            post.canonical_url,
                            content_body?.source ?? null,
                          )
                          return embedUrl ? (
                            <div className="mb-4">
                              <div className="bg-secondary_subtle aspect-video w-full overflow-hidden rounded-lg">
                                <iframe
                                  src={embedUrl}
                                  title={post.title}
                                  className="h-full w-full border-0"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                  allowFullScreen
                                  loading="lazy"
                                />
                              </div>
                              {/* Fallback link in case iframe is blocked or for unknown sources */}
                              <div className="mt-2 text-center">
                                <a
                                  href={post.canonical_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-brand-primary text-sm hover:underline"
                                >
                                  Open in new tab
                                </a>
                              </div>
                            </div>
                          ) : (
                            <div className="bg-secondary_subtle mb-4 flex items-center justify-center rounded-lg p-8">
                              <p className="text-tertiary text-sm">
                                Content cannot be embedded.{" "}
                                <a
                                  href={post.canonical_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-brand-primary hover:underline"
                                >
                                  View original
                                </a>
                              </p>
                            </div>
                          )
                        })()}
                      {content_body.caption && (
                        <div>
                          <h3 className="text-tertiary mb-1 text-sm font-medium">
                            Caption
                          </h3>
                          <p className="text-md text-secondary whitespace-pre-wrap">
                            {content_body.caption}
                          </p>
                        </div>
                      )}
                      {content_body.transcript && (
                        <div>
                          <h3 className="text-tertiary mb-1 text-sm font-medium">
                            Transcript
                          </h3>
                          <p className="text-md text-secondary whitespace-pre-wrap">
                            {content_body.transcript}
                          </p>
                        </div>
                      )}
                    </>
                  )}

                  {/* Article Body */}
                  {contentType == ContentBodyTypeEnum.article &&
                    content_body.body && (
                      <>
                        <h2 className="text-primary mb-4 text-lg font-semibold">
                          Article
                        </h2>
                        <TextHTML text={content_body.body} />
                      </>
                    )}

                  {/* Photo Body */}
                  {contentType == ContentBodyTypeEnum.photo && (
                    <div className="flex flex-col gap-4">
                      {content_body.image_url && (
                        <div className="flex justify-center">
                          <img
                            src={content_body.image_url}
                            alt={content_body.alt_text ?? post.title}
                            className="h-auto max-w-full rounded-lg"
                          />
                        </div>
                      )}
                      {post.canonical_url && (
                        <div className="text-center">
                          <a
                            href={post.canonical_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-brand-primary inline-flex items-center gap-2 text-sm hover:underline"
                          >
                            <LinkExternal01 className="size-4" />
                            View Original Source
                          </a>
                        </div>
                      )}
                      {content_body.caption && (
                        <div>
                          <h3 className="text-tertiary mb-1 text-sm font-medium">
                            Caption
                          </h3>
                          <p className="text-md text-secondary whitespace-pre-wrap">
                            {content_body.caption}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Notes */}
            {track.notes && (
              <div className="border-secondary bg-primary rounded-xl border p-6">
                <h2 className="text-primary mb-3 text-lg font-semibold">
                  Notes
                </h2>
                <TextHTML text={track.notes} />
              </div>
            )}

            {/* Suggested content — tracks elsewhere that share a tag */}
            {relatedTracks.length > 0 && (
              <div className="flex flex-col gap-4">
                <h2 className="text-primary text-lg font-semibold">
                  Suggested content
                </h2>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {relatedTracks.map((relatedTrack) => (
                    <RelatedTrackCard
                      key={relatedTrack.id}
                      track={relatedTrack}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Companion aside — "About this track". Sticky on desktop so the
              metadata stays with the reader as the content scrolls. */}
          <aside className="flex flex-col gap-5 lg:sticky lg:top-4 lg:self-start">
            <AboutThisTrack
              playlist={playlist}
              tags={track.tags}
              contentTypeLabel={contentTypeLabel}
              source={content_body?.source ?? null}
              aspectLabel={aspectLabel}
              durationLabel={durationLabel}
              readingLabel={readingLabel}
            />
          </aside>
        </div>
      </div>
    </AuthedShell>
  )
}

/**
 * The companion aside's masthead card — where the track lives and its content
 * metadata, echoing the header beside the scrolling content. Grammar matches
 * the "About this list" card on playlists#show.
 */
function AboutThisTrack({
  playlist,
  tags,
  contentTypeLabel,
  source,
  aspectLabel,
  durationLabel,
  readingLabel,
}: {
  playlist: Playlist
  tags: Track["tags"]
  contentTypeLabel: string | null
  source: string | null
  aspectLabel: string | null
  durationLabel: string | null
  readingLabel: string | null
}) {
  const rows = [
    { label: "Type", value: contentTypeLabel },
    { label: "Source", value: source },
    { label: "Aspect", value: aspectLabel },
    { label: "Duration", value: durationLabel },
    { label: "Reading", value: readingLabel },
  ].filter((row) => row.value)

  return (
    <div className="border-secondary bg-primary flex flex-col gap-5 rounded-xl border p-5">
      <h2 className="text-tertiary text-xs font-semibold tracking-wide uppercase">
        About this track
      </h2>

      <div className="flex flex-col gap-1">
        <span className="text-tertiary text-xs">In playlist</span>
        <Link
          href={`/playlists/${playlist.slug ?? playlist.id}`}
          className="text-primary text-sm font-medium hover:underline"
        >
          {playlist.title}
        </Link>
      </div>

      {rows.length > 0 && (
        <dl className="flex flex-col gap-2 text-sm">
          {rows.map((row) => (
            <div
              key={row.label}
              className="flex items-center justify-between gap-4"
            >
              <dt className="text-tertiary">{row.label}</dt>
              <dd className="text-secondary font-medium">{row.value}</dd>
            </div>
          ))}
        </dl>
      )}

      {tags.length > 0 && (
        <div className="flex flex-col gap-2">
          <h3 className="text-tertiary text-xs font-semibold tracking-wide uppercase">
            Tags
          </h3>
          <TagBadgeGroup tags={tags} />
        </div>
      )}
    </div>
  )
}
